import React, { useState, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { AbstractCourse } from '../../../../../api/types/settings/studies/abstract-course';
import dateUtils from '../../../../../utils/date-format';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { AddNewAbstractCourseDialog } from './components/add-new-abstract-course-dialog';
import { deleteAbstractCoursesOperation } from './operations/delete-abstract-courses-operation';
import { UpdateNewAbstractCourseDialog } from './components/update-abstract-course-dialog';
import { InputText } from 'primereact/inputtext';
import appApi from '../../../../../api/app-api';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';

const ITEMS_PER_PAGE = 10;

// Separate search component to avoid observer re-renders affecting input performance
const SearchInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = React.memo(({ value, onChange }) => (
  <div style={{ position: 'relative', flex: 1 }}>
    <InputText
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="חיפוש קורסים לפי שם, מגמה, שעות או אחוז נוכחות..."
      style={{
        width: '100%',
        height: '30px',
        paddingLeft: '40px',
        direction: 'rtl',
      }}
      autoComplete="off"
    />
    <i
      className="pi pi-search"
      style={{
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#6c757d',
        pointerEvents: 'none',
      }}
    />
  </div>
));

const SettingsEducationAbstractCourses = observer(() => {
  const [displayAddDialog, setDisplayAddDialog] = useState(false);

  // Pagination state
  const [abstractCourses, setAbstractCourses] = useState<AbstractCourse[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(ITEMS_PER_PAGE);

  // Sidebar filter (server-side)
  const [courseNameFilter, setCourseNameFilter] = useState<string>('');
  const [selectedMajorIds, setSelectedMajorIds] = useState<number[]>([]);
  const [appliedCourseNameFilter, setAppliedCourseNameFilter] =
    useState<string>('');
  const [appliedMajorIds, setAppliedMajorIds] = useState<number[]>([]);
  const skipNextAutoLoadRef = useRef(false);

  const loadAbstractCourses = useCallback(
    async (
      offset: number,
      limit: number,
      filters?: { courseName?: string; majorIds?: number[] },
    ) => {
      setTableLoading(true);
      try {
        const response = await appApi.getAbstractCoursesPaginated(
          offset,
          limit,
          filters?.courseName,
          filters?.majorIds ?? [],
        );
        if (response.data.body) {
          setAbstractCourses(response.data.body.data);
          setTotalRecords(response.data.body.total);
        }
      } catch (error) {
        console.error('Error loading abstract courses:', error);
      } finally {
        setTableLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (skipNextAutoLoadRef.current) {
      skipNextAutoLoadRef.current = false;
      return;
    }

    loadAbstractCourses(first, rows, {
      courseName: appliedCourseNameFilter,
      majorIds: appliedMajorIds,
    });
  }, [
    first,
    rows,
    loadAbstractCourses,
    appliedCourseNameFilter,
    appliedMajorIds,
  ]);

  const onPageChange = (event: DataTablePageEvent) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const refreshData = () => {
    loadAbstractCourses(first, rows, {
      courseName: appliedCourseNameFilter,
      majorIds: appliedMajorIds,
    });
  };

  const [deleteAbstractId, setDeleteAbstractId] = useState<number | null>(null);
  const handleDeleteProgram = async (abstractCourseId: number) => {
    await deleteAbstractCoursesOperation(abstractCourseId, setDeleteAbstractId);
    refreshData();
  };
  const [editAbstractCourse, setEditAbstractCourse] =
    useState<AbstractCourse | null>(null);

  const [loading, setLoading] = useState(false);
  const handleEditAssignment = (abstractCourse: AbstractCourse) => {
    setEditAbstractCourse(abstractCourse);
  };

  const handleDialogHide = (shouldRefresh?: boolean) => {
    setDisplayAddDialog(false);
    if (shouldRefresh) {
      refreshData();
    }
  };

  const handleEditDialogHide = (shouldRefresh?: boolean) => {
    setEditAbstractCourse(null);
    if (shouldRefresh) {
      refreshData();
    }
  };

  // Search functionality
  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');

  // Memoized search handler to prevent unnecessary re-renders
  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  // Debounce search input to avoid filtering on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchText]);

  // Filter courses based on debounced search text (client-side filtering on current page)
  const filteredCourses = abstractCourses.filter((course) => {
    if (!debouncedSearchText.trim()) {
      return true;
    }

    const searchLower = debouncedSearchText.toLowerCase().trim();

    // Search in course name
    if (course.courseName.toLowerCase().includes(searchLower)) {
      return true;
    }

    if (
      course.majors.some((major) =>
        major.majorName.toLowerCase().includes(searchLower),
      )
    ) {
      return true;
    }

    // Search in course hours (convert to string)
    if (course.courseHours.toString().includes(searchLower)) {
      return true;
    }

    // Search in attendance percentage (convert to string)
    if (course.attendancePercentage.toString().includes(searchLower)) {
      return true;
    }

    // Search in creator name
    if (course.createdByName.toLowerCase().includes(searchLower)) {
      return true;
    }

    return false;
  });

  const handleMajorToggle = (id: number) => {
    setSelectedMajorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSideBarSearch = async () => {
    const nextCourseName = courseNameFilter.trim();
    const nextMajorIds = selectedMajorIds;

    skipNextAutoLoadRef.current = true;
    setAppliedCourseNameFilter(nextCourseName);
    setAppliedMajorIds(nextMajorIds);
    setFirst(0);

    await loadAbstractCourses(0, rows, {
      courseName: nextCourseName,
      majorIds: nextMajorIds,
    });
  };

  const handleSideBarClearAll = async () => {
    skipNextAutoLoadRef.current = true;
    setCourseNameFilter('');
    setSelectedMajorIds([]);
    setAppliedCourseNameFilter('');
    setAppliedMajorIds([]);
    setFirst(0);

    await loadAbstractCourses(0, rows, { courseName: '', majorIds: [] });
  };

  const sideBarStyles = {
    container: {
      height: '100%',
      background: 'white',
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column' as const,
      direction: 'rtl' as const,
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      gap: '8px',
    },
    content: {
      flex: 1,
      overflowY: 'auto' as const,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
      paddingRight: '4px',
    },
    buttonContainer: {
      marginTop: '16px',
      padding: '8px',
      borderTop: '1px solid #e9ecef',
    },
    input: {
      width: '100%',
      height: '36px',
      direction: 'rtl' as const,
    },
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
        flexDirection: 'row',
      }}
    >
      <Dialog
        header="הוספת קורס טיפוסי"
        visible={displayAddDialog}
        onHide={() => handleDialogHide()}
        style={{
          width: '500px',
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
      >
        <AddNewAbstractCourseDialog onHide={() => handleDialogHide(true)} />
      </Dialog>

      <Dialog
        header="עריכת קורס טיפוסי"
        visible={editAbstractCourse !== null}
        onHide={() => handleEditDialogHide()}
        style={{
          width: '500px',
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
      >
        <UpdateNewAbstractCourseDialog
          onHide={() => handleEditDialogHide(true)}
          abstractCourseId={editAbstractCourse?.id}
          courseHours={editAbstractCourse?.courseHours}
          name={editAbstractCourse?.courseName}
          coursePercentage={editAbstractCourse?.attendancePercentage}
        />
      </Dialog>

      {/* Sidebar filter (right side) */}
      <div style={{ width: '340px', flexShrink: 0, marginTop: '16px' }}>
        <div style={sideBarStyles.container}>
          <div style={sideBarStyles.headerRow}>
            <div
              style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#333' }}
            >
              סינונים
            </div>
            <Button
              icon="pi pi-filter-slash"
              text
              severity="secondary"
              tooltip={'נקה הכל'}
              onClick={handleSideBarClearAll}
            />
          </div>

          <div style={sideBarStyles.content}>
            <CustomExpander>
              <div>שם קורס</div>
              <div style={{ padding: '8px' }}>
                <InputText
                  value={courseNameFilter}
                  onChange={(e) => setCourseNameFilter(e.target.value)}
                  placeholder="חפש קורס..."
                  style={sideBarStyles.input}
                  autoComplete="off"
                />
              </div>
            </CustomExpander>

            <FilterExpanderWithListOfCheckboxes
              title="מגמות"
              items={globalSettingsStores.majors}
              selectedItems={selectedMajorIds}
              onItemToggle={handleMajorToggle}
              getItemLabel={(major) => major.majorName}
              getItemKey={(major) => major.id}
              defaultExpanded={true}
              searchPlaceholder="חפש מגמה..."
            />
          </div>

          <div style={sideBarStyles.buttonContainer}>
            <Button
              label="חפש"
              icon="pi pi-search"
              onClick={handleSideBarSearch}
              style={{ width: '100%' }}
              loading={tableLoading}
            />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            marginTop: '16px',
          }}
        >
          <SearchInput value={searchText} onChange={handleSearchChange} />

          <Button
            onClick={() => setDisplayAddDialog(true)}
            style={{ height: '30px', flexShrink: 0 }}
            label={'הוספה'}
          />
        </div>

        <DataTable
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          value={filteredCourses}
          dataKey="id"
          tableStyle={{
            direction: 'rtl',
            marginTop: '16px',
          }}
          loading={tableLoading}
          paginator
          lazy
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPage={onPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        >
          <Column
            style={{ textAlign: 'right', height: '40px' }}
            field="courseName"
            header="שם"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
          />

          <Column
            style={{ textAlign: 'right', height: '40px' }}
            header="מגמות"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
            body={(rowData: AbstractCourse) => (
              <div>
                {rowData.majors && rowData.majors.length > 0
                  ? rowData.majors.map((major) => major.majorName).join(', ')
                  : 'אין מגמות'}
              </div>
            )}
          />

          <Column
            style={{ textAlign: 'right', height: '40px' }}
            field="courseHours"
            header="כמות שעות"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
          />

          <Column
            style={{ textAlign: 'right', height: '40px' }}
            field="courseHours"
            header="אחוז נוכחות"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
            body={(rowData: AbstractCourse) => (
              <div>{rowData.attendancePercentage}%</div>
            )}
          />

          <Column
            style={{ textAlign: 'right', height: '40px' }}
            field="createdAt"
            header="תאריך יצירה"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
            body={(rowData: AbstractCourse) => (
              <div>{dateUtils.displayFormatDate(rowData.createdAt)}</div>
            )}
          />

          <Column
            style={{ textAlign: 'right' }}
            field="נשלח ע״י"
            header=""
            body={(data: AbstractCourse) => (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  icon="pi pi-pencil"
                  text
                  severity="warning"
                  tooltip={'עריכת סוג מטלה'}
                  onClick={() => handleEditAssignment(data)}
                />
                <Button
                  icon="pi pi-trash"
                  text
                  severity="danger"
                  tooltip={'מחיקת תוכנית'}
                  loading={deleteAbstractId === data.id}
                  disabled={deleteAbstractId !== null}
                  onClick={() => handleDeleteProgram(data.id)}
                />
              </div>
            )}
          />
        </DataTable>
      </div>
    </div>
  );
});

export default SettingsEducationAbstractCourses;
